import { SimpleExpression } from './expression';

// ===== Enums =====

export enum FeeRuleType {
  Initial = 'Initial',
  RequestValue = 'RequestValue',
  Final = 'Final',
  IncludeFee = 'IncludeFee',
}

export enum FeeCostType {
  Fixed = 'Fixed',
  Percentage = 'Percentage',
}

export enum FeePercentageBasedOn {
  BaseLoanAmount = 'BaseLoanAmount',
  TotalLoanAmount = 'TotalLoanAmount',
}

export enum PaidTo {
  None = 'None',
  Broker = 'Broker',
  Lender = 'Lender',
  Seller = 'Seller',
  Investor = 'Investor',
  Affiliate = 'Affiliate',
  Other = 'Other',
}

export enum FeeToleranceLevel {
  None = 'None',
  Zero = 'Zero',
  TenPercent = 'TenPercent',
}

export enum FeesDocumentType {
  PDF = 'PDF',
}

export enum FeeUpdateType {
  SetValue = 'SetValue',
  AddValue = 'AddValue',
  RemoveValue = 'RemoveValue',
}

export enum IntegrationVendor {
  None = 'None',
  Factual = 'Factual',
  Xactus = 'Xactus',
  InformativeResearch = 'InformativeResearch',
  MeridianLink = 'MeridianLink',
  FreddieMac = 'FreddieMac',
  FannieMae = 'FannieMae',
  RatesBoard = 'RatesBoard',
  Mezzo = 'Mezzo',
  Vesta = 'Vesta',
  Resend = 'Resend',
  Snap = 'Snap',
  Truv = 'Truv',
  Equifax = 'Equifax',
  Truework = 'Truework',
  Argyle = 'Argyle',
  Ernst = 'Ernst',
  SmartFees = 'SmartFees',
  Empower = 'Empower',
  Encompass = 'Encompass',
  Experian = 'Experian',
}

// ===== View Models =====

export interface FeeConfigurationViewModel {
  feeConfigurationId: number;
  feeConfigurationGuid: string;
  feeRulesVersionId: number | null;
  feeHudLineId: number;
  description: string | null;
  rules: FeeRuleViewModel[] | null;
}

export interface FeeHudLineViewModel {
  feeHudLineId: number;
  feeRulesVersionId: number | null;
  losVendor: IntegrationVendor | null;
  hudLine: string | null;
  hudSection: string | null;
  description: string | null;
  loanEstimateSection: string | null;
  closingDisclosureSection: string | null;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo | null;
  restrictions: FeeRestrictionsViewModel | null;
  losMetadata: FeeLosMetadataConfigurationViewModel[] | null;
}

export interface FeeRuleViewModel {
  ruleId: string;
  description: string | null;
  expression: SimpleExpression | null;
  ruleType: FeeRuleType | null;
  settingData: SettingDataViewModel[] | null;
}

export interface DateValueViewModel {
  date: string | null;
  value: string | null;
}

export interface SettingDataViewModel {
  fieldId: string | null;
  value: string | null;
  updateType?: FeeUpdateType;
  isDateBased?: boolean;
  dateFieldPath?: string | null;
  dateValues?: DateValueViewModel[];
}

export interface FeeRulesVersionViewModel {
  feeRulesVersionId: number;
  versionNumber: number;
  description: string | null;
  publishDate: string | null;
  feeConfig: FeeConfigurationViewModel[] | null;
}

export interface FeeRestrictionsViewModel {
  toleranceLevel: FeeToleranceLevel;
  canEditBorrowerAmount: boolean;
  canEditSellerAmount: boolean;
  canEditBrokerAmount: boolean;
  canEditLenderAmount: boolean;
  canEditIncrementDollarAmount: boolean;
  canEditOtherAmount: boolean;
  canClientIgnore: boolean;
}

export interface FeeLosMetadataConfigurationViewModel {
  losFieldId: string | null;
  fieldName: string | null;
  value: string | null;
}

export interface FeeLosMetadataValueViewModel {
  losFieldId: string | null;
  value: string | null;
}

export interface FeeChargesViewModel {
  totalFeeAmount: number;
  totalFeeCostType: FeeCostType;
  totalFeePercentBasedOn: FeePercentageBasedOn;
  totalFeePercentage: number | null;
  minFeeAmount: number | null;
  maxFeeAmount: number | null;
  borrowerFeeCostType: FeeCostType;
  borrowerFeeAmount: number;
  borrowerFeePercentage: number | null;
  borrowerPaidOutsideClosing: number;
  sellerFeeCostType: FeeCostType;
  sellerFeeAmount: number;
  sellerFeePercentage: number | null;
  sellerPaidOutsideClosing: number;
  thirdPartyFeeCostType: FeeCostType;
  thirdPartyFeeAmount: number;
  thirdPartyFeePercentage: number | null;
  lenderFeeCostType: FeeCostType;
  lenderFeeAmount: number;
  lenderFeePercentage: number | null;
}

export interface FeeViewModel {
  feeConfigurationId: number | null;
  hudLine: string | null;
  hudSection: string | null;
  description: string | null;
  loanEstimateSection: string | null;
  closingDisclosureSection: string | null;
  group: string | null;
  groupDetails: string | null;
  isApr: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo;
  alwaysReturnFeeInResponse: boolean;
  isFeeValid: boolean;
  serviceProviderId: string | null;
  serviceType: string | null;
  charges: FeeChargesViewModel;
  restrictions: FeeRestrictionsViewModel;
  losMetadataValues: FeeLosMetadataValueViewModel[] | null;
}

export interface FeesDocumentViewModel {
  name: string | null;
  base64Data: string | null;
  documentType: FeesDocumentType;
}

export interface AppraisalProductViewModel {
  productId: string | null;
  productDescription: string | null;
}

export interface PropertyTaxBillViewModel {
  installments: TaxInstallmentsViewModel;
}

export interface TaxInstallmentsViewModel {
  numberOfInstallments: number | null;
  firstInstallmentAmount: number | null;
  secondInstallmentAmount: number | null;
  thirdInstallmentAmount: number | null;
  fourthInstallmentAmount: number | null;
}

export interface TaxPayeeViewModel {
  payeeId: string | null;
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface TaxEscrowReservesViewModel {
  months: number | null;
  amount: number | null;
}

export interface PropertyTaxLineViewModel {
  taxAmount: number | null;
  lineType: string | null;
  taxYear: number | null;
  priorYearDue: number | null;
  projectedTax: number | null;
  supplementalTax: number | null;
  receivedDate: string | null;
  dueDate: string | null;
  delinquent: boolean;
  propertyExemption: string | null;
  paymentStatus: string | null;
  taxPayee: TaxPayeeViewModel | null;
  taxBill: PropertyTaxBillViewModel[] | null;
  escrowReserves: TaxEscrowReservesViewModel | null;
}

export interface PropertyTaxesViewModel {
  taxLines: PropertyTaxLineViewModel[] | null;
}

export interface SettlementServiceProviderViewModel {
  name: string | null;
  address: string | null;
  phoneNumber: string | null;
  email: string | null;
}

export interface ProviderResponseViewModel {
  providerName: string | null;
  responseData: string | null;
}

export interface LoanFeesSupplementalDataViewModel {
  closingDate: string | null;
  appraisalProducts: AppraisalProductViewModel[] | null;
  propertyTaxes: PropertyTaxesViewModel | null;
  settlementServiceProviders: SettlementServiceProviderViewModel[] | null;
  providerResponses: ProviderResponseViewModel[] | null;
  documents: FeesDocumentViewModel[] | null;
}

export interface FeesResponseViewModel {
  transactionId: string | null;
  fees: FeeViewModel[] | null;
  supplementalData: LoanFeesSupplementalDataViewModel | null;
}

export interface FeeResponseViewModel {
  transactionId: string | null;
  productName: string | null;
  fees: FeeViewModel[] | null;
  supplementalData: LoanFeesSupplementalDataViewModel | null;
  errors: string[] | null;
}

// ===== Question Response Defaults =====

export interface QuestionResponseDefaultViewModel {
  questionResponseDefaultId: number;
  questionId: number;
  questionKey: string | null;
  vendor: IntegrationVendor;
  defaultAnswer: boolean;
  defaultValue: string | null;
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface CreateOrUpdateQuestionResponseDefaultViewModel {
  questionResponseDefaultId?: number | null;
  questionId: number;
  vendor: IntegrationVendor;
  defaultAnswer: boolean;
  defaultValue: string | null;
}

// ===== Request Models =====

export interface CreateOrUpdateFeeConfigRequestViewModel {
  feeHudLineId: number;
  description: string | null;
  rules: FeeRuleViewModel[] | null;
}

export interface CreateOrUpdateFeeHudLineViewModel {
  losVendor: IntegrationVendor;
  hudLine: string;
  hudSection: string;
  description: string;
  loanEstimateSection: string;
  closingDisclosureSection: string;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo;
  restrictions: FeeRestrictionsViewModel;
  losMetadata: FeeLosMetadataConfigurationViewModel[];
}

export interface PublishFeeRulesVersionRequestViewModel {
  description: string;
  publishDate?: string | null;
}

// ===== Helper Types =====

export function isDraftConfiguration(config: FeeConfigurationViewModel): boolean {
  return config.feeRulesVersionId === null;
}

export function isDraftHudLine(hudLine: FeeHudLineViewModel): boolean {
  return hudLine.feeRulesVersionId === null;
}

export function isPublishedConfiguration(config: FeeConfigurationViewModel): boolean {
  return config.feeRulesVersionId !== null;
}

export function isPublishedHudLine(hudLine: FeeHudLineViewModel): boolean {
  return hudLine.feeRulesVersionId !== null;
}

// ===== Import Models =====

export interface ExcelImportRowError {
  rowNumber: number;
  errorMessage: string | null;
  rowIdentifier: string | null;
}

export interface ExcelImportResult {
  success: boolean;
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  errors: ExcelImportRowError[] | null;
  warnings: ExcelImportRowError[] | null;
}

// ===== Migration Models =====

export interface FeeHudLineExport {
  hudLine: string;
  hudSection: string;
  description: string;
  loanEstimateSection: string;
  closingDisclosureSection: string;
  losVendor: IntegrationVendor;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo | null;
  restrictions: FeeRestrictionsViewModel;
  losMetadata: FeeLosMetadataConfigurationViewModel[];
}

export interface FeeConfigurationExport {
  feeConfigurationGuid: string;
  hudLine: string;
  description: string | null;
  rules: FeeRuleExport[];
}

export interface FeeRuleExport {
  ruleId: string;
  description: string;
  expression: string | null;
  ruleType: FeeRuleType;
  settingData: SettingDataExport[];
}

export interface SettingDataExport {
  fieldId: string;
  value: string | null;
  updateType: FeeUpdateType;
  isDateBased?: boolean;
  dateFieldPath?: string | null;
  dateValues?: DateValueViewModel[];
}

export interface FeeMigrationExport {
  hudLines: FeeHudLineExport[];
  configurations: FeeConfigurationExport[];
}

export enum DiffAction {
  Insert = 'Insert',
  Update = 'Update',
  Delete = 'Delete',
}

export interface FeeMigrationHudLineDiff {
  action: DiffAction;
  hudLine: string;
  imported: FeeHudLineExport | null;
  current: FeeHudLineExport | null;
  changedFields: string[];
}

export interface FeeMigrationRuleDiff {
  action: DiffAction;
  ruleId: string;
  description: string;
  imported: FeeRuleExport | null;
  current: FeeRuleExport | null;
  changedFields: string[];
}

export interface FeeMigrationConfigDiff {
  action: DiffAction;
  feeConfigurationGuid: string;
  hudLine: string;
  imported: FeeConfigurationExport | null;
  current: FeeConfigurationExport | null;
  changedFields: string[];
  ruleDiffs: FeeMigrationRuleDiff[];
}

export interface FeeMigrationDiff {
  hudLineDiffs: FeeMigrationHudLineDiff[];
  configurationDiffs: FeeMigrationConfigDiff[];
}

export interface FeeMigrationImportRequest {
  hudLineDiffs: FeeMigrationHudLineDiff[];
  configurationDiffs: FeeMigrationConfigDiff[];
}

export interface FeeMigrationImportResult {
  success: boolean;
  hudLinesInserted: number;
  hudLinesUpdated: number;
  hudLinesDeleted: number;
  configurationsInserted: number;
  configurationsUpdated: number;
  configurationsDeleted: number;
  errors: string[];
}
